import { Text } from "@mantine/core";
import { Fragment } from "react";
import Header from "src/components/shared/EntriesGenerator/Header";
import { useShowContent } from "src/generator/hooks/useShowContent";
import { getCellWidth } from "src/generator/utils/getCellWidth";
import { useTheme } from "src/hooks";
import { type ICol } from "src/interfaces";
import { StyledSplitTh, StyledTh } from "src/styled";
import { v4 as uuidv4 } from "uuid";

import RowCell from "../RowCell";

interface Props {
  row: { cols: ICol[]; isDraftEntry?: boolean } | ICol[];
  history?: boolean;
  isOverlay?: boolean;
  rowIndex?: number;
  rowsLength?: number;
  lbeid?: string;
  showSubRowHeaders?: boolean;
  headers?: ICol[];
  subRowIndex?: number;
  entryIndex?: number;
  divider?: boolean;
  pdfEntry?: boolean;
  layout?: number[];
  siblingRows?: any[];
  // Section and row position, set where a cell can show its trend.
  trendRow?: string;
}

const Row = ({
  row,
  history,
  isOverlay,
  rowIndex,
  rowsLength,
  lbeid,
  showSubRowHeaders,
  headers,
  subRowIndex,
  entryIndex,
  divider,
  pdfEntry,
  layout,
  siblingRows,
  trendRow,
}: Props) => {
  const theme = useTheme() as any;
  const { shouldHideElement } = useShowContent();

  const data = row ? ("cols" in row ? row.cols : row) : undefined;
  const rowIndexForBorders = subRowIndex ?? rowIndex;
  let columnOffset = 0;
  // Counts hidden cells too, like the chart data does.
  let leafOffset = 0;
  const shouldGenerateSubRowHeaders =
    subRowIndex !== undefined &&
    ((rowIndex === 0 && subRowIndex > 0) || rowIndex !== 0) &&
    showSubRowHeaders &&
    headers &&
    Array.isArray(headers[subRowIndex]);

  const content = (
    <Fragment>
      {shouldGenerateSubRowHeaders &&
        !shouldHideElement(headers[subRowIndex], entryIndex) && (
          <Header
            headers={headers[subRowIndex] as any}
            sIndex={-1}
            headerSize=""
          />
        )}
      {data?.map?.((col: ICol, index: number) => {
        const colAny = col as any;
        const colSpan = colAny.colspan
          ? colAny.colspan
          : colAny.span
            ? 1 + colAny.span
            : !colAny.full
              ? 1
              : 999;
        const currentLeafOffset = leafOffset;
        leafOffset += colSpan;

        if (
          colAny?.feature &&
          colAny?.feature === "hidden" &&
          colAny?.onClass
        ) {
          if (shouldHideElement(colAny, entryIndex)) return null;
        }

        const currentColumnOffset = columnOffset;
        columnOffset += colSpan;

        // Header styled cells
        if (colAny.headerStyle) {
          const title = [];
          if (colAny.name && colAny.name.indexOf("<br>") > -1) {
            const arr = colAny.name.split("<br>");
            arr.forEach((v: string) =>
              title.push(<StyledSplitTh key={uuidv4()}>{v}</StyledSplitTh>),
            );
          } else {
            title.push(colAny.name);
          }

          return (
            <StyledTh
              key={`header-cell-${index * Math.random()}`}
              colSpan={colSpan}
              style={{ minWidth: getCellWidth(colAny.width) }}>
              <Text inherit c="inherit" span style={{ position: "relative" }}>
                {title}
              </Text>
            </StyledTh>
          );
        }

        return (
          <RowCell
            key={`row-cell-${index * Math.random()}`}
            col={col}
            span={colSpan}
            index={index}
            isOverlay={isOverlay}
            isDraftEntry={(row as any).isDraftEntry}
            history={history}
            rowIndex={rowIndex}
            rowsLength={rowsLength}
            lbeid={lbeid}
            entryIndex={entryIndex}
            pdfEntry={pdfEntry}
            layout={layout}
            subRowIndex={subRowIndex}
            siblingRows={siblingRows}
            rowIndexForBorders={rowIndexForBorders}
            columnOffset={currentColumnOffset}
            trendCell={
              trendRow === undefined
                ? undefined
                : `${trendRow}:${currentLeafOffset}`
            }
          />
        );
      })}
    </Fragment>
  );
  if (shouldGenerateSubRowHeaders)
    return (
      <tr style={{ border: "none" }}>
        <td style={{ padding: 0, border: "none" }} colSpan={999}>
          <table
            style={{
              width: "100%",
              borderSpacing: 0,
              borderCollapse: "collapse",
            }}>
            {content}
          </table>
        </td>
      </tr>
    );

  return (
    <tr
      style={{
        backgroundColor: theme?.colors.tdBackgroundColor[0],
        ...(divider
          ? { borderTop: `2px solid ${theme?.colors.grayscaleBorder[0]}` }
          : {}),
      }}>
      {content}
    </tr>
  );
};

export default Row;
